import React, { useEffect, useRef } from 'react';
import Intercom, { show, shutdown, update } from '@intercom/messenger-js-sdk';
import moment from 'moment';
import css from "./IntercomComponent.module.css"

function isMobileLayout() {
  if (typeof window !== "undefined") {
    return window.innerWidth <= 768;
  }
  return false; // default if running server-side
};


/**
 * Safely sends an event to Intercom.
 *
 * @param {string} event_name - The name of the Intercom event (must be string).
 * @param {Object} metadata - Optional metadata object to send with the event.
 */
export const IntercomSendEvents = (event_name, metadata = {}) => {
  if (
    typeof window === 'undefined' ||
    typeof Intercom !== 'function' ||
    typeof event_name !== 'string' ||
    !event_name.trim()
  ) {
    console.warn('⚠️ Intercom event not sent: invalid environment or event name.', { event_name });
    return;
  }

  try {
    Intercom('trackEvent', event_name, metadata);
    console.log(`✅ Intercom event sent: ${event_name}`, metadata);
  } catch (err) {
    console.error(`❌ Failed to send Intercom event: ${event_name}`, err);
  }
};

/**
 * Reinitializes Intercom with user data when a user interacts (e.g., clicks).
 * Only boots Intercom if the user is valid (not deleted or banned) and essential data exists.
 *
 * @param {Object} user - The user object containing ID and attributes.
 */
export const reinitilizeIntercomOnClickEvents = (user) => {
  try {
    const app_id = process.env.REACT_APP_INTERCOM_APP_ID;
    if (!app_id) {
      console.warn('Intercom App ID is missing from environment variables.');
      return;
    }
    const { id, attributes } = user || {};
    const {
      email,
      createdAt,
      deleted,
      banned,
      profile = {}
    } = attributes || {};

    const { firstName, lastName, publicData = {} } = profile;
    const {
      userRole
    } = publicData;


    const userId = id?.uuid;
    const userName = `${firstName || ''} ${lastName || ''}`.trim();
    // Validate user has required data and is not banned/deleted
    const isValidUser = !!userId && !!email && !deleted && !banned;

    if (!isValidUser) {
      console.info('User is invalid or inactive. Intercom will not be initialized.');
      return;
    }

    Intercom({
      app_id,
      user_id: userId,
      name: userName,
      email: email,
      created_at: moment(createdAt).unix(),
      user_role: userRole,
      hide_default_launcher: isMobileLayout() ? true : false
    });

  } catch (error) {
  }
};

/**
 * Safely shuts down the Intercom session.
 * This is typically used when the user logs out or switches accounts.
 */
export const shutdownIntecomOnClickEvents = () => {
  try {
    // Ensure Intercom is available before attempting to shut down
    if (typeof shutdown === 'function') {
      shutdown();
      console.log('Intercom has been shut down successfully.');
    } else {
      console.warn('Intercom shutdown function is not available.');
    }
  } catch (error) {
    console.error('Failed to shut down Intercom:', error);
  }
}

/**
 * Opens the Intercom Messenger when triggered (e.g., on 'Contact Us' click).
 * Make sure Intercom has been booted before calling this.
 */
export const openIntecomOnClickEvents = () => {
  try {
    if (typeof show === 'function') {
      show();
      console.log('Intercom Messenger opened.');
    } else {
      console.warn('Intercom show function is not available.');
    }
  } catch (error) {
    console.error('Failed to open Intercom Messenger:', error);
  }
};

/**
 * IntercomComponent - Initializes Intercom using the Messenger SDK for valid users.
 * Only runs once when the component is mounted and user is valid.
 */
export default function IntercomComponent({ user, nonLoggedInUserEmail }) {
  if (typeof window == 'undefined') return <></>;

  const app_id = process.env.REACT_APP_INTERCOM_APP_ID;

  // Refs to remember the last initialized user ID or visitor email
  const prevUserIdRef = useRef(null);
  const prevEmailRef = useRef(null);

  // Destructure user info
  const { id, attributes } = user || {};
  const {
    email,
    createdAt,
    deleted,
    banned,
    profile = {}
  } = attributes || {};

  const { firstName, lastName, publicData = {} } = profile;
  const {
    userRole
  } = publicData;

  const userId = id?.uuid;
  const userName = `${firstName || ''} ${lastName || ''}`.trim();
  const isValidUser = !!userId && !!email && !deleted && !banned;

  useEffect(() => {
    // CASE 1: Logged-in user is present
    if (isValidUser) {
      // If this is a different user than before, restart Intercom
      if (prevUserIdRef.current !== userId) {
        // If any previous Intercom session exists (user or email), shut it down first
        if (prevUserIdRef.current || prevEmailRef.current) {
          console.log('&&& &&& => Logged-in user: shutdown ');
          shutdown();  // Clear any existing Intercom session
        }
        console.log('&&& &&& => Logged-in user: Initialize');
        // Boot Intercom with the new user's details
        Intercom({
          app_id,
          user_id: userId,
          name: userName,
          email: email,
          created_at: moment(createdAt).unix(),
          user_role: userRole,
          hide_default_launcher: isMobileLayout() ? true : false
        });
        // Update refs to record the current session
        prevUserIdRef.current = userId;
        prevEmailRef.current = null;
      } else {
        console.log('&&& &&& => Logged-in user: Upadte');
        // Same user ID as before: update any changed fields (e.g. name or email)
        update({
          name: userName,
          email: email,
          user_role: userRole
        });
      }
    }


    // CASE 2: No logged-in user, but we have a visitor email
    else if (!user && nonLoggedInUserEmail) {
      // If this is a new visitor email, restart Intercom session as a visitor
      if (prevEmailRef.current !== nonLoggedInUserEmail) {
        if (prevUserIdRef.current || prevEmailRef.current) {
          console.log('&&& &&& =>  No logged-in user : shutdown');
          shutdown();  // Clear previous session
        }
        console.log('&&& &&& =>No logged-in user : Initialize');
        Intercom({
          app_id: app_id,
          email: nonLoggedInUserEmail
        });
        prevEmailRef.current = nonLoggedInUserEmail;
        prevUserIdRef.current = null;
      }
      // No update() needed here since visitor session typically has only email
    }


    // CASE 3: Neither user nor visitor email – ensure Intercom is shut down
    else {
      if (prevUserIdRef.current || prevEmailRef.current) {
        console.log('&&& &&& => shutdown');
        shutdown();
        prevUserIdRef.current = null;
        prevEmailRef.current = null;
      }
    }
  }, [
    userId,       // run effect when user ID changes
    userName,     // or when user’s name changes (to update)
    email,    // or when user’s email changes (to update)
    nonLoggedInUserEmail // or when fallback email changes
  ]);

  return <></>;
}
